import { defineDocument, type LineGridDef } from './types.js'
import { ALL_TABS, BASIC_ACTIONS, baseColumns, baseFilters, col, f } from './common.js'

/**
 * docs/09 — tools are SERIALISED. Every line carries identifiers, because the
 * business tracks the individual drill, not a count of drills.
 */
const toolLines = (title: string, n: string, qtyLabel = 'Quantity'): LineGridDef => ({
  title, n,
  columns: [
    col.seq(), col.item('Tool'), col.desc(),
    { key: 'toolUnitId', label: 'Tool unit', type: 'text', width: 190, editable: true, lookup: { entity: 'tool_unit' },
      hint: 'The individual tool being moved. Its holder and condition follow this document.' },
    { key: 'identifiers', label: 'Identifier(s)', type: 'text', width: 180, editable: true },
    col.qty(qtyLabel), col.unit(),
  ],
  sources: [{ key: 'excel', label: 'Import from Excel', source: 'excel' as const }],
})

export const toolEntry = defineDocument({
  key: 'tool_entry',
  group: 'Tools and Equipment',
  blurb: 'Add new tools to your tool inventory, or remove broken tools.',
  route: 'tool_entries',
  label: 'Tool entry',
  labelPlural: 'Tool entries',
  numbering: 'TOOL_ENTRY',
  numberPrefix: 'TE-',
  workflow: 'TOOL_ENTRY',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Tool entry',
    fields: [
      f.radio('entryType', 'Adding or removing?', [['ADD', 'Add tools'], ['REMOVE', 'Remove broken tools']]),
      f.look('warehouseId', 'Warehouse', 'warehouse', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.choice('entryType', 'Adding or removing?', [['ADD', 'Add tools'], ['REMOVE', 'Remove broken tools']]),
          lockAfterDraft: true },
        f.look('warehouseId', 'Warehouse', 'warehouse', true),
        f.date('documentDate', 'Date', true),
        f.look('supplierId', 'Supplied by', 'supplier'),
        f.text('reason', 'Reason', 'full'),
        f.notes(),
      ] },
    ],
  },
  lines: toolLines('Tools', '02', 'Quantity'),
  columns: [
    ...baseColumns(null, 'Value'),
    { key: 'entryType', label: 'Direction', type: 'text', width: 130 },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: [],
} as const)

export const toolRequest = defineDocument({
  key: 'tool_request',
  group: 'Tools and Equipment',
  blurb: 'Request tools for your projects or service work.',
  route: 'tool_reqs',
  label: 'Tool request',
  labelPlural: 'Tool requests',
  numbering: 'TOOL_REQUEST',
  numberPrefix: 'TR-',
  workflow: 'TOOL_REQUEST',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Request tools',
    fields: [
      f.radio('requestFor', 'What are they for?', [
        ['PROJECT', 'A project'],
        ['INDIVIDUAL_TOOLKIT', 'Somebody’s own toolkit'],
      ]),
      { ...f.look('projectId', 'Project', 'project', true, 'full'),
        visibleWhen: { field: 'requestFor', equals: ['PROJECT'] } },
      { ...f.date('requiredDate', 'Required by', true, 'full'),
        visibleWhen: { field: 'requestFor', equals: ['PROJECT'] } },
      f.look('issuedToId', 'Issue to', 'employee', true, 'full'),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        f.choice('requestFor', 'What are they for?', [['PROJECT', 'A project'], ['INDIVIDUAL_TOOLKIT', 'An individual toolkit']], false),
        { ...f.look('projectId', 'Project', 'project'),
          visibleWhen: { field: 'requestFor', equals: ['PROJECT'] } },
        f.date('documentDate', 'Request date', true),
        { ...f.date('requiredDate', 'Required by'),
          visibleWhen: { field: 'requestFor', equals: ['PROJECT'] } },
        f.look('issuedToId', 'Issue to', 'employee'),
        f.look('requestedById', 'Requested by', 'employee'),
        { ...f.date('returnBy', 'Expected back by'),
          visibleWhen: { field: 'requestFor', equals: ['PROJECT'] },
          hint: 'A toolkit stays with the person, so it has no return date.' },
        f.notes(),
      ] },
    ],
  },
  lines: {
    title: 'Tools wanted', n: '02',
    columns: [
      col.seq(), col.item('Tool'), col.desc(), col.qty(), col.unit(),
      { key: 'issuedQty', label: 'Issued', type: 'qty', width: 100, align: 'right' },
      { key: 'openQty', label: 'Still to issue', type: 'qty', width: 128, align: 'right' },
      { key: 'preClosedQty', label: 'Pre closed', type: 'qty', width: 118, align: 'right' },
    ],
    sources: [{ key: 'excel', label: 'Import from Excel', source: 'excel' }],
  },
  columns: [
    ...baseColumns(null, 'Value'),
    { key: 'fulfilmentStatus', label: 'Issue status', type: 'text', width: 190 },
  ],
  filters: baseFilters(null),
  actions: [...BASIC_ACTIONS, 'preClose', 'undoPreClose'],
  rules: [],
  createsNext: [{ docType: 'tool_issue', label: 'Tool issue', whenStatus: 'APPROVED' }],
} as const)

export const toolIssue = defineDocument({
  key: 'tool_issue',
  group: 'Tools and Equipment',
  blurb: 'Issue tools against a request, or record the return of tools.',
  route: 'tool_issues',
  label: 'Tool issue',
  labelPlural: 'Tool issues',
  numbering: 'TOOL_ISSUE',
  numberPrefix: 'TI-',
  workflow: 'TOOL_ISSUE',
  tabs: ALL_TABS,
  createWizard: {
    title: 'Tool issue',
    subtitle: 'Issuing tools out, or recording them coming back.',
    fields: [
      f.radio('issueType', 'Issue or return?', [['ISSUE', 'Issue'], ['RETURN', 'Return']]),
      { ...f.look('toolRequestId', 'Tool request', 'tool_request', true),
        visibleWhen: { field: 'issueType', equals: ['ISSUE'] } },
      f.look('warehouseId', 'Warehouse', 'warehouse', true),
      f.look('issuedToId', 'Issue to', 'employee', true),
    ],
  },
  form: {
    sections: [
      { key: 'general', n: '01', title: 'General information', fields: [
        { ...f.choice('issueType', 'Issue or return?', [['ISSUE', 'Issue'], ['RETURN', 'Return']]),
          lockAfterDraft: true },
        f.look('toolRequestId', 'Against request', 'tool_request'),
        f.look('warehouseId', 'Warehouse', 'warehouse', true),
        f.look('issuedToId', 'Issued to', 'employee', true),
        f.look('projectId', 'Project', 'project'),
        f.date('documentDate', 'Date', true),
        f.date('returnBy', 'Due back'),
        // docs/09 — a fitter's own kit goes out with them and is not tracked
        // back to a warehouse shelf.
        f.yesNo('includePersonalToolkit', 'Includes their personal toolkit?'),
        f.notes(),
      ] },
    ],
  },
  lines: toolLines('Tools issued', '02', 'Issued'),
  columns: [
    ...baseColumns(null, 'Value'),
    { key: 'issueType', label: 'Direction', type: 'text', width: 130 },
    { key: 'fulfilmentStatus', label: 'Return status', type: 'text', width: 190 },
  ],
  filters: [
    ...baseFilters(null),
    { key: 'issuedToId', label: 'Issued to', control: 'lookup', lookup: { entity: 'employee' } },
    { key: 'warehouseId', label: 'Warehouse', control: 'lookup', lookup: { entity: 'warehouse' } },
  ],
  actions: BASIC_ACTIONS,
  rules: [],
  allocatesFrom: ['tool_request'],
} as const)
